import { getConfig } from "../config";

/**
 * THE HTTP CLIENT — a single one, against three in V1 (two RTK Query caches plus
 * an imperative class-based client).
 *
 * It does three things only: prefix the API address, carry the token, and turn a
 * failed response into a usable error. Caching and retries belong to TanStack
 * Query, not here.
 */

export const TOKEN_KEY = "gk-token";

export const token = {
  read(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      // Private browsing, storage blocked: carry on without a session rather than
      // bringing the application down.
      return null;
    }
  },
  save(value: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, value);
    } catch {
      /* storage unavailable: the session will not survive a reload */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing to do */
    }
  },
};

/**
 * THE ENVELOPE. This API wraps everything in `{ status, message, data }`, and the
 * payload every caller wants is `data`. V1 unwraps it in its own client
 * (`fetchAsync` returns `result.data`); doing it anywhere else means every caller
 * remembering, and the one who forgets gets `undefined` with no clue why. Signing
 * in read the token from the wrong level and announced a service outage on a
 * perfectly successful login.
 *
 * ⚠️ THE TWO APIS DO NOT WRAP THE SAME WAY. api-v1 answers
 * `{ status, message, data }`; apix answers `{ data }` and nothing else. The
 * first rule here required `status`, so apix responses were never unwrapped —
 * the language catalogue came back empty, no event settings could be keyed, and
 * the panel silently went on showing seeded text while saving to the real
 * database.
 *
 * So an envelope is recognised by what it does NOT carry: a `data` key, and
 * otherwise nothing but `status` and `message`. A body with a `data` field of
 * its own alongside anything else is left whole, which is the case this
 * detection exists to protect.
 */
const ENVELOPE = new Set(["status", "message", "data"]);

export function unwrap(body: unknown): unknown {
  if (body && typeof body === "object" && !Array.isArray(body)) {
    const shape = body as Record<string, unknown>;
    const keys = Object.keys(shape);
    if (keys.includes("data") && keys.every((k) => ENVELOPE.has(k))) {
      return shape.data;
    }
  }
  return body;
}

/** API error: carries the HTTP status so callers can decide. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * REFUSED — 401 or 403.
   *
   * ⚠️ TWO DIFFERENT THINGS WEAR THE SAME STATUS, and the caller has to know which
   * it is holding. On a read, it usually means the session is gone and signing in
   * again fixes it. On a WRITE from the panel it means the opposite: the session is
   * perfectly valid and the account simply has no right to write — signing out
   * would lose the work and change nothing. Whoever handles this decides from
   * WHERE it came, not from the number.
   */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

type Options = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /**
   * `keepalive` laisse la requête survivre à la page — pour le vidage déclenché quand
   * l'onglet se masque ou se ferme, qu'un `fetch` normal avorte au déchargement. Les
   * corps de réglages restent bien sous les 64 Ko qu'un navigateur accorde à une telle
   * requête.
   */
  keepalive?: boolean;
  /**
   * Which of the two APIs. `v1` resolves the context and signs people in; `apix`
   * holds the settings and the languages. Named rather than passed as a URL, so
   * a caller cannot quietly aim at a third address.
   */
  api?: "v1" | "apix";
};

/**
 * THE SAME CALL, WITH THE RESPONSE HEADERS KEPT.
 *
 * ⚠️ A SEPARATE FUNCTION RATHER THAN ONE MORE OPTION, for the reason `apiBlob` gives just
 * below: the return type changes, and a flag that silently reshapes what a caller gets
 * back is a flag someone will forget. `apiFetch` delegates here, so there is still only
 * one place that knows about the address, the token and the errors.
 *
 * What it exists for: a paginated route cannot put its total in the body without changing
 * the shape every other caller depends on, so it sends it as a header instead.
 */
export async function apiFetchWithHeaders<T>(
  path: string,
  options: Options = {},
): Promise<{ data: T; headers: Headers }> {
  const { method = "GET", body, signal, api = "v1", keepalive } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined)
    headers["Content-Type"] = "application/json; charset=utf-8";

  const bearer = token.read();
  if (bearer) headers.Authorization = `Bearer ${bearer}`;

  // Read HERE, not at import: a module that reads its configuration merely
  // by being imported takes the test suite down with it. See `config/index.ts`.
  const config = getConfig();
  const base = api === "apix" ? config.apixUrl : config.apiUrl;
  const response = await fetch(`${base}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
    ...(keepalive ? { keepalive: true } : {}),
  });

  if (!response.ok) {
    // Try to surface the server's own message; fall back to the status.
    let message = `${response.status} ${response.statusText}`;
    try {
      const data = (await response.json()) as { message?: string };
      if (data?.message) message = data.message;
    } catch {
      /* non-JSON response: keep the status */
    }
    throw new ApiError(response.status, path, message);
  }

  // 204 and friends: no body to parse.
  if (response.status === 204)
    return { data: undefined as T, headers: response.headers };
  return { data: unwrap(await response.json()) as T, headers: response.headers };
}

export async function apiFetch<T>(
  path: string,
  options: Options = {},
): Promise<T> {
  const { data } = await apiFetchWithHeaders<T>(path, options);
  return data;
}

/**
 * LA MÊME CHOSE, POUR CE QUI N'EST PAS DU JSON.
 *
 * ⚠️ UNE FONCTION À PART PLUTÔT QU'UNE OPTION DE PLUS, parce que le type de retour
 * change : `apiFetch` promet un objet déballé de son enveloppe, et un PDF n'a ni
 * enveloppe ni forme. Mélangés, l'appelant aurait reçu un `T` générique qui n'est pas
 * ce qu'il croit — et `response.json()` sur un flux binaire échoue par une erreur de
 * syntaxe qui ne dit rien de la cause.
 *
 * Le reste est partagé : même adresse, même jeton, mêmes erreurs typées.
 */
export async function apiBlob(
  path: string,
  options: Omit<Options, "body"> & { body?: unknown } = {},
): Promise<Blob> {
  const { method = "GET", body, signal, api = "v1" } = options;

  const headers: Record<string, string> = { Accept: "application/pdf" };
  if (body !== undefined)
    headers["Content-Type"] = "application/json; charset=utf-8";

  const bearer = token.read();
  if (bearer) headers.Authorization = `Bearer ${bearer}`;

  const config = getConfig();
  const base = api === "apix" ? config.apixUrl : config.apiUrl;
  const response = await fetch(`${base}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const data = (await response.json()) as { message?: string };
      if (data?.message) message = data.message;
    } catch {
      /* réponse non-JSON : on garde le statut */
    }
    throw new ApiError(response.status, path, message);
  }

  return response.blob();
}
